<!-- 展会+交易商-  实勘详情页 大组件 -->
<script setup lang="ts">
import { FALLBACK_DOWNLOAD_LINKS, getAppDownloadLinks, normalizeDownloadLinks } from '@/api/download'
import { getSurveyDetails, type SurveyDetailData } from '@/api/survey'
import { pingGoogle } from '~/server/common'
const { t, localePath } = useAppI18n()
const route = useRoute()
const { getBreadcrumb } = useLsBreadcrumb()
const is404 = ref(false)
const EXPO_RIGHT_ICON = 'https://resources1.interface003.com/web30/2026/06/26/cd3c933c-8a96-462d-996e-ee1b92d8554e.png'
type InspectionDetailPageVariant = 'default' | 'expo'

type BreadcrumbSourceItem = {
  route: string
  label?: string
  labelKey?: string
  color?: string
}

type InspectionPageAsyncData = {
  downloadLinks: ReturnType<typeof normalizeDownloadLinks>
  surveyDetailData: SurveyDetailData | null
}

/** 组件入参，控制当前详情页对应的路由类型。 */
const props = withDefaults(
  defineProps<{
    pageVariant?: InspectionDetailPageVariant
  }>(),
  {
    pageVariant: 'default',
  },
)

/** 从动态路由参数中提取当前实勘 slug。 */
const routeSlug = computed(() => {
  const slug = route.params.slug
  if (Array.isArray(slug)) {
    return slug[0] || ''
  }

  return typeof slug === 'string' ? slug : ''
})

/** 获取实勘详情与下载地址，首屏 SSR 与站内导航 CSR 复用同一入口。 */
const { data: pageData, error: pageDataError } = await useAsyncData(
  () => `survey-details:${props.pageVariant}:${routeSlug.value}`,
  async (): Promise<InspectionPageAsyncData> => {
    const [surveyDetailsResponse, downloadLinksResponse] = await Promise.all([
      getSurveyDetails({ urlPath: routeSlug.value }),
      getAppDownloadLinks().catch(() => null),
    ])
    const surveyDetailData = surveyDetailsResponse?.data ?? null

    if (!surveyDetailData?.detail) {
      throw createError({
        statusCode: 404,
        statusMessage: 'Page Not Found',
      })
    }

    return {
      downloadLinks: normalizeDownloadLinks(downloadLinksResponse),
      surveyDetailData,
    }
  },
  {
    default: () => ({
      downloadLinks: FALLBACK_DOWNLOAD_LINKS,
      surveyDetailData: null,
    }),
    watch: [routeSlug],
  },
)

if (pageDataError.value) {
  throw pageDataError.value
}

if (!pageData.value?.surveyDetailData?.detail) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Page Not Found',
  })
}

/** 客户端路由切换时，将 useAsyncData 捕获的接口异常转成 404 页面。 */
watch(pageDataError, (error) => {
  if (error) {
    showError(error)
  }
})

/** 当前页面的实勘详情数据。 */
const surveyData = computed<SurveyDetailData>(() => pageData.value?.surveyDetailData ?? {})
/** 右侧下载卡片使用的 App 下载地址。 */
const downloadLinks = computed(() => pageData.value?.downloadLinks ?? FALLBACK_DOWNLOAD_LINKS)

/** 实勘主体信息。 */
const surveyInfo = computed(() => surveyData.value?.detail ?? {})
/** 热门实勘列表。 */
const hotSurveyList = computed(() => surveyData.value?.top ?? [])

/** 地图弹窗显示状态。 */
const showMapDialog = ref(false)

/** VR 弹窗显示状态。 */
const showVrDialog = ref(false)

/** 视频弹窗显示状态。 */
const showVideoDialog = ref(false)

/** 图片预览弹窗显示状态。 */
const showImgDialog = ref(false)

/** 当前视频弹窗使用的视频地址。 */
const currentVideoUrl = ref('')

/** 当前视频弹窗使用的封面图地址。 */
const currentVideoPoster = ref('')

/** 当前图片弹窗打开时的初始索引。 */
const currentImgIndex = ref(0)

/** 富文本内容容器引用，用于绑定图片预览事件。 */
const htmlContentRef = ref<HTMLElement | null>(null)

/** 实勘关联的交易商信息。 */
const surveyTrader = computed(() => surveyInfo.value?.related ?? {})
/** 当前实勘是否为展会类型。 */
const isExhibition = computed(() => Boolean(surveyInfo.value?.isexhibition))

const exhibitionObj = computed(() => surveyInfo.value?.exhibition ?? {})
// console.log('exhibitionObj::: ', exhibitionObj.value)

/** SEO 标题中使用的交易商名称，优先本地化名称。 */
const seoTraderName = computed(() => surveyInfo.value?.trader?.localName || surveyInfo.value?.trader?.defaultName || '')

/** SEO 展会名称，兼容接口大小写字段。 */
const seoExpoName = computed(
  () => exhibitionObj.value?.expoName || exhibitionObj.value?.ExpoName || surveyInfo.value?.title || '',
)

/**
 * 解析 SEO 多语言文案并替换占位符。
 * @param key 多语言 key。
 * @param values 占位符替换值。
 * @returns 替换后的 SEO 文案。
 */

/** 替换占位符后的 SEO 标题。 */
const seoTitle = computed(() =>
  isExhibition.value ? t('84364', [seoExpoName.value]) : t('84401', [seoTraderName.value]),
)

/** 替换占位符后的 SEO 描述。 */
const seoDescription = computed(() =>
  isExhibition.value
    ? t('84366', [seoExpoName.value, seoExpoName.value])
    : t('84393', [seoTraderName.value, seoTraderName.value]),
)

useHead(() => ({
  title: seoTitle.value,
  meta: [
    {
      name: 'description',
      content: seoDescription.value,
    },
  ],
}))

/** 校验当前数据是否匹配页面路由类型，不匹配时返回 404。 */
const validatePageVariant = async () => {
  if (!surveyInfo.value?.title) {
    return
  }

  const isVariantMatched = props.pageVariant === 'expo' ? isExhibition.value : !isExhibition.value

  if (!isVariantMatched) {
    await navigateTo(
      localePath({
        name: 'inspections-expo-exchange-slug',
        params: { slug: routeSlug.value },
      }),
      { redirectCode: 301 },
    )
  }
}

/** 展会扩展信息。 */
const exhibitionInfo = computed(() => surveyInfo.value?.exhibition ?? {})

/** 展会地点文案，优先城市，其次国家。 */
const exhibitionPlace = computed(() => surveyInfo.value?.city || surveyInfo.value?.country || '')

/** 展会场景头图标题。 */
const exhibitionHeadline = computed(() => {
  const traderName = surveyInfo.value?.company || surveyTrader.value?.shortName || ''
  const expoName = exhibitionInfo.value?.expoName || ''

  if (!traderName || !expoName) {
    return surveyInfo.value?.title || ''
  }

  return `${expoName}`
})

/** 展会头部元信息配置。 */
const exhibitionMetaItems = computed(() =>
  [
    {
      key: 'address',
      label: t('84282'),
      value: surveyInfo.value?.address || '',
      icon: 'https://resources1.interface003.com/web30/2026/06/23/4e93fd79-54dd-4ea2-bd1c-58a8704702bd.png',
    },
    {
      key: 'time',
      label: t('84283'),
      value: formatTimestamp(exhibitionInfo.value?.exhibition_time),
      icon: 'https://resources1.interface003.com/web30/2026/06/23/fd8fc5ab-209d-43d8-8bc2-22508313306e.png',
    },
    {
      key: 'booth',
      label: t('84284'),
      value: exhibitionInfo.value?.position || '',
      icon: 'https://resources1.interface003.com/web30/2026/06/23/a2192ce9-96ac-4119-ad8c-37f90b4e5838.png',
    },
  ].filter((item) => item.value),
)

/** 修正后的富文本内容。 */
const surveyContent = computed(() => fixedContent(surveyInfo.value?.content || ''))

/** 从富文本正文中提取出的图片预览源列表。 */
const surveyContentImages = computed(() => {
  const imageSet = new Set<string>()
  const parser = import.meta.client ? new DOMParser() : null

  if (parser && surveyContent.value) {
    const doc = parser.parseFromString(surveyContent.value, 'text/html')

    doc.querySelectorAll('img').forEach((img) => {
      const src = img.getAttribute('src')?.trim() || ''
      if (src) {
        imageSet.add(src)
      }
    })
  }

  return Array.from(imageSet)
})

/** 根据用户所在国家判断当前页面默认使用的地图类型。 */
/** Google 是否可访问。 */
const isGoogleMap = ref(false)

/** 根据地图类型切换地图封面图。 */
const isGoogleMapCover = computed(() => (isGoogleMap.value ? surveyInfo.value.googleImg : surveyInfo.value.baiduImg))

/** 当前弹窗实际使用的地图类型。 */
const useGoogleMapInDialog = computed(() => isGoogleMap.value)

/** 客户端缓存的上一页来源面包屑，新开页面时从 localStorage 读取。 */
const sourceBreadcrumb = ref<BreadcrumbSourceItem | null>(null)

/** 读取当前详情页对应的来源面包屑。 */
const refreshSourceBreadcrumb = () => {
  sourceBreadcrumb.value = getBreadcrumb(routeSlug.value)
}

/** 按当前语言解析第二级面包屑文案，优先使用缓存中的翻译 key。 */
const resolveSourceBreadcrumbLabel = (breadcrumb: BreadcrumbSourceItem | null) => {
  if (!breadcrumb) {
    return ''
  }

  if (breadcrumb.labelKey) {
    return t(breadcrumb.labelKey)
  }

  return breadcrumb.label || ''
}

/** 第二级面包屑仅在客户端读到来源记录后才渲染。 */
const surveySourceBreadcrumb = computed<BreadcrumbSourceItem | null>(() => sourceBreadcrumb.value)

/** 当前页面的面包屑配置。 */
const surveyBreadcrumbItems = computed(() => {
  const breadcrumbItems: Array<{ label: string; route?: string; color?: string }> = [
    {
      label: t('82520'),
      route: '/',
      color: isExhibition.value ? 'rgba(255,255,255,0.6)' : '#7D818A',
    },
  ]

  if (surveySourceBreadcrumb.value?.route) {
    breadcrumbItems.push({
      label: resolveSourceBreadcrumbLabel(surveySourceBreadcrumb.value),
      route: surveySourceBreadcrumb.value.route,
      color: isExhibition.value ? 'rgba(255,255,255,0.6)' : '#7D818A',
    })
  }

  breadcrumbItems.push({
    label: t('84048'),
    color: isExhibition.value ? '#FFF' : '#292a2e',
  })

  return breadcrumbItems
})

/**
 * 打开视频弹窗。
 * @param url 视频地址。
 * @returns 无返回值。
 */
const openVideoDialog = (url: string) => {
  if (!url) {
    return
  }

  currentVideoUrl.value = url
  currentVideoPoster.value = surveyInfo.value.vcover || ''
  showVideoDialog.value = true
}

/**
 * 打开 VR 弹窗。
 * @param vrm VR 数据对象或直接传入的 VR 地址。
 * @returns 无返回值。
 */
const openVrDialog = (vrm?: { url?: string } | string) => {
  const targetUrl = typeof vrm === 'string' ? vrm : vrm?.url

  if (!targetUrl) {
    return
  }

  showVrDialog.value = true
}

/**
 * 打开图片预览弹窗。
 * @param index 需要预览的图片索引。
 * @returns 无返回值。
 */
const openImgDialog = (index: number) => {
  currentImgIndex.value = index
  showImgDialog.value = true
}

/** 打开地图弹窗。 */
const openMapDialog = () => {
  showMapDialog.value = true
}

/** 为富文本中的图片补充预览所需的样式和 data 属性。 */
const bindContentImages = async () => {
  await nextTick()

  const container = htmlContentRef.value
  if (!container) {
    return
  }

  container.querySelectorAll('img').forEach((img) => {
    const imageElement = img as HTMLImageElement
    imageElement.classList.add('cursor-pointer')
    imageElement.setAttribute('data-preview-src', imageElement.currentSrc || imageElement.src || '')
  })
}

/**
 * 处理富文本点击事件，命中图片时打开预览弹窗。
 * @param event 点击事件对象。
 * @returns 无返回值。
 */
const handleContentClick = (event: MouseEvent) => {
  const target = event.target
  if (!(target instanceof HTMLImageElement)) {
    return
  }

  const imageSrc = target.getAttribute('data-preview-src') || target.currentSrc || target.src || ''
  if (!imageSrc) {
    return
  }

  const imageIndex = surveyContentImages.value.findIndex((item) => item === imageSrc)

  openImgDialog(imageIndex >= 0 ? imageIndex : 0)
}

/**
 * 修正服务端下发的富文本内容。
 * @param txt 原始富文本字符串。
 * @returns 处理后的富文本内容，缺失时返回 `--`。
 */
const fixedContent = (txt: string) => {
  if (txt) {
    txt = txt && txt.replace(/<p>　　/g, '<p>') // eslint-disable-line
    txt = txt && txt.replace(/-wikifx_articlepic/g, '-article598').replace(/<img/g, '<img loading="lazy"')
    txt =
      txt && txt.replace(/<table([^>]*)>(.*?)<\/table>/gis, '<div class="table-container"><table$1>$2</table></div>')
    txt =
      txt &&
      txt.replace(
        /<p>(?![^<]*class=)((?:[^<]|<(?!\/p>))*?<strong>.*?<\/strong>(?:[^<]|<(?!\/p>))*?)<\/p>/g,
        '<p class="title">$1</p>',
      )
    return txt
  }

  return '--'
}

watch(
  surveyInfo,
  () => {
    validatePageVariant()
  },
  {
    immediate: true,
  },
)

watch(
  surveyContent,
  () => {
    if (!import.meta.client) {
      return
    }

    bindContentImages()
  },
  {
    immediate: true,
  },
)

onMounted(async () => {
  refreshSourceBreadcrumb()
  bindContentImages()
  isGoogleMap.value = await pingGoogle()
})

watch(routeSlug, () => {
  if (!import.meta.client) {
    return
  }
  refreshSourceBreadcrumb()
})

const { setBreadcrumb } = useLsBreadcrumb()
const setBreadcrumbFunc = () => {
  setBreadcrumb(routeSlug.value, {
    labelKey: '84048',
    route: route.fullPath,
  })
}
</script>

<template>
  <div>
    <CommonNotFound v-if="is404" />
    <div v-else>
      <section v-if="isExhibition" class="relative mt-[70px] overflow-hidden">
        <div class="absolute top-0 left-1/2 h-[408px] w-full max-w-none -translate-x-1/2 overflow-hidden object-cover">
          <CommonImage
            src="https://resources1.interface003.com/web30/2026/06/16/678456da-0ce6-417d-9801-7f5cac1cace7.png"
            alt="exhibition hero"
            class="size-full object-cover"
            loading="eager"
          />
        </div>
        <div class="absolute inset-0"></div>
        <div class="relative mx-auto flex h-[408px] w-[clamp(800px,100%,1200px)] flex-col">
          <div class="flex items-center text-[14px] leading-[22px] text-[rgba(255,255,255,0.6)]">
            <CommonBreadcrumb :items="surveyBreadcrumbItems" />
          </div>

          <div class="absolute top-0 right-0 flex size-[96px] rtl:right-auto rtl:left-0">
            <CommonImage
              src="https://resources1.interface003.com/web30/2026/06/16/ef97d632-f0f8-40f7-b74b-17157dec0ca8.png"
              alt="WikiEXPO"
              class="size-full object-cover"
              loading="eager"
            />
          </div>

          <div class="flex flex-1 flex-col items-start justify-start py-[28px]">
            <div class="flex flex-col gap-[20px]">
              <div class="flex items-center gap-[8px]">
                <div class="flex size-[24px] overflow-hidden rounded-[2px]">
                  <CommonImage
                    :src="surveyInfo.ico"
                    :alt="surveyInfo.company || surveyTrader.shortName"
                    class="size-full object-cover"
                    loading="eager"
                  />
                </div>
                <span class="text-[20px] leading-[28px] font-[600] text-white">
                  {{ surveyInfo.company || surveyTrader.shortName || '--' }}
                </span>
              </div>

              <NuxtLink
                class="flex items-center gap-[12px]"
                target="_blank"
                :to="
                  localePath({
                    name: 'inspections-expo-exchange-slug-index',
                    params: { slug: surveyInfo?.urlPath || '' },
                  })
                "
                @click="setBreadcrumbFunc"
              >
                <h1 class="max-w-[1000px] truncate text-[36px] leading-[44px] font-[600] text-white">
                  {{ exhibitionHeadline }}
                </h1>

                <CommonImage
                  :src="EXPO_RIGHT_ICON"
                  class="size-[28px] shrink-0 rtl:rotate-180"
                  alt="icon"
                  loading="eager"
                />
              </NuxtLink>

              <div class="flex items-center gap-[8px]">
                <div class="rounded-[2px] bg-[#1f845a] px-[8px] py-[2px] text-[13px] leading-[20px] text-white">
                  {{ surveyInfo.evaluate || 'Diamond Sponsors' }}
                </div>
                <div class="flex items-center gap-[4px] rounded-[2px] bg-[rgba(255,255,255,0.2)] px-[8px] py-[2px]">
                  <div class="flex h-[14px] w-[22px] overflow-hidden rounded-[1px]">
                    <CommonImage
                      :src="surveyInfo.flag"
                      :alt="exhibitionPlace"
                      class="size-full object-cover"
                      loading="eager"
                    />
                  </div>
                  <span class="text-[13px] leading-[20px] text-white">
                    {{ exhibitionPlace }}
                  </span>
                </div>
              </div>

              <div class="flex flex-col gap-[8px] pt-[37px]">
                <div
                  v-for="item in exhibitionMetaItems"
                  :key="item.key"
                  class="flex items-center gap-[8px] text-[#b7b9be]"
                >
                  <div class="flex size-[20px] items-center justify-center overflow-hidden">
                    <CommonImage :src="item.icon" :alt="item.label" class="object-cover" loading="eager" />
                  </div>
                  <span class="text-[16px] leading-[24px]">
                    {{ item.label }}：
                    <span :dir="item.key === 'time' ? 'ltr' : ''" class="ftf">
                      {{ item.value }}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div
        class="survey-container mx-auto w-[clamp(800px,calc(100%-64px),1200px)] pb-[36px]"
        :class="isExhibition ? 'pt-[32px]' : 'pt-[70px]'"
      >
        <CommonBreadcrumb v-if="!isExhibition" :items="surveyBreadcrumbItems" />
        <div
          class="flex w-full justify-between gap-x-[20px] max-[1000px]:flex-col"
          :class="isExhibition ? '' : 'pt-[20px]'"
        >
          <div class="max-w-[752px] min-w-0 flex-1 max-[1000px]:max-w-full">
            <div class="border-b-[0.5px] border-solid border-[rgba(5,21,36,0.06)] pb-[20px]">
              <template v-if="!isExhibition">
                <div class="title leading--[36px] text-[28px] leading-[36px] font-[600] text-[#292a2e]">
                  {{ surveyInfo.title }}
                </div>
                <div
                  class="mt-[16px] flex items-center justify-between border-b-[0.5px] border-solid border-[rgba(5,21,36,0.06)] pb-[20px]"
                >
                  <div class="flex items-center gap-[12px]">
                    <div
                      v-if="surveyInfo.evaluate"
                      :style="{
                        background: surveyInfo.evaluatecolor,
                      }"
                      class="inline-flex items-center rounded-[2px] px-[8px] py-[2px] text-[12px] leading-[18px] text-white"
                    >
                      {{ surveyInfo.evaluate }}
                    </div>

                    <div class="flex items-center gap-[4px]">
                      <CommonImage
                        class="size-[20px]"
                        src="https://resources1.interface003.com/web30/2026/06/11/c1e3e37f-9ec4-4e86-a4c1-7a5798d383f1.png"
                        alt="location"
                        loading="eager"
                      />
                      <span class="text-[14px] leading-[22px] text-[#7d818a]">{{ surveyInfo.country }}</span>
                    </div>

                    <span dir="ltr" class="ftf text-[14px] leading-[22px] text-[#7d818a]">{{
                      formatTimestamp(surveyInfo.wiki_timestamp)
                    }}</span>
                  </div>
                  <div class="relative z-[10]">
                    <CommonArticleShareGroup :title="surveyInfo.title" />
                  </div>
                </div>
              </template>
              <div :class="isExhibition ? '' : 'mt-[20px]'">
                <SurveySwiper :info="surveyInfo" @open-video-dialog="openVideoDialog" @open-vr-dialog="openVrDialog" />
              </div>
              <div class="relative mt-[20px] cursor-pointer overflow-hidden rounded-[8px]" @click="openMapDialog">
                <CommonImage
                  class="block h-[128px] w-full object-cover"
                  :src="isGoogleMapCover"
                  :alt="useGoogleMapInDialog ? 'googleMap' : 'baiduMap'"
                  loading="eager"
                />
                <div
                  v-if="surveyInfo.address"
                  class="absolute right-0 bottom-0 rounded-tl-[8px] rounded-br-[8px] bg-[rgba(5,12,31,0.46)] px-[8px] py-[4px] rtl:right-auto rtl:left-0 rtl:rounded-tl-[0px] rtl:rounded-tr-[8px] rtl:rounded-br-[0px] rtl:rounded-bl-[8px]"
                >
                  <div class="text-[12px] leading-[18px] whitespace-nowrap text-white">
                    {{ surveyInfo.address }}
                  </div>
                </div>
                <SurveyLocal
                  :is-expo="isExhibition"
                  class="absolute top-1/2 left-1/2 z-[1] translate-x-[-50%] translate-y-[-50%]"
                />
              </div>
            </div>

            <div
              ref="htmlContentRef"
              class="html-content-box mt-[20px] border-b-[0.5px] border-solid border-[rgba(5,21,36,0.06)] pb-[8px]"
              v-html="surveyContent"
              @click="handleContentClick"
            ></div>

            <div v-if="!isExhibition" class="mt-[20px]">
              <div class="mb-[16px] text-[18px] leading-[26px] font-[600] text-[#292a2e]">
                {{ t('84324') }}
              </div>

              <SurveyHots :hot-survey-list="hotSurveyList" />
            </div>
          </div>
          <div
            class="flex max-w-[384px] min-w-0 shrink-0 flex-col gap-[20px] max-[1000px]:mt-[20px] max-[1000px]:grid max-[1000px]:max-w-full max-[1000px]:grid-cols-1 max-[1000px]:flex-row"
          >
            <SurveyTrader v-if="surveyTrader?.alias" :info="surveyTrader" />

            <div class="sticky top-[90px] max-[1000px]:hidden">
              <CommonDownload :download-links="downloadLinks" />
            </div>
          </div>
        </div>

        <SurveyMapDialog v-model="showMapDialog" :item="surveyInfo" :use-google-map="useGoogleMapInDialog" />
        <SurveyVrDialog v-model="showVrDialog" :item="surveyInfo" />
        <SurveyVideoDialog v-model="showVideoDialog" :address="currentVideoUrl" :poster="currentVideoPoster" />
        <SurveyImgDialog v-model="showImgDialog" :images="surveyContentImages" :initial-index="currentImgIndex" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="less">
@import '@/assets/less/richText.less';
</style>
